/**
 * [AI補完仕様] このコメントはAI補完（コード自動生成）に構造情報として渡されます。変更時は実装品質に直結するため正確に保つこと。
 *
 * 認証コンテキスト - ログイン状態管理・認証APIコール。トークン自体の保持・更新はtoken-storeに委譲する。
 *
 * @contract 認証状態の参照にはuseAuth()を使うこと。tokenを直接操作しない。
 * @contract API呼び出しにはapi-client（useApiClient）を使うこと。useAuthFetchは下位互換用。
 * @contract アクセストークンの更新（自動リフレッシュ含む）はtoken-store（setAccessToken/clearTokens/refreshAccessToken）経由で行う。
 * @contract /auth/login, /auth/refresh, /auth/logout はRefresh Tokenをcookieでやり取りするためwithCredentials: trueを必ず指定すること。
 * @contract /auth/change-password はAuthorization: Bearerで呼び出すこと（Cookie/CSRFベースのエンドポイントとして扱わない）。
 * @contract isInitializing中はセッション復元（Cookie経由のサイレントログイン）判定中を表す。ProtectedRouteはこの間LoginPageを表示しないこと。
 * @signature AuthProvider: FC<{ children }> - アプリルートに配置済み
 * @signature useAuth(): { user, token, login(username, password): Promise<void>, logout(): Promise<void>, isAuthenticated: boolean, isInitializing: boolean, changePassword(currentPassword, newPassword): Promise<void> }
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useSyncExternalStore, useRef } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken, setAccessToken, clearTokens, subscribeTokens, refreshAccessToken, getCsrfToken } from './token-store';

interface AuthUser {
  id: string;
  username: string;
  roles: string[];
  displayName: string;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;

  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;

  isAuthenticated: boolean;
  isInitializing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = '/api';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // token-store が単一の情報源。ここでは購読するだけで直接更新しない
  // （axiosインターセプター等のReact外のコードからも同じ値を参照・更新できるようにするため）。
  const queryClient = useQueryClient();
  const token = useSyncExternalStore(subscribeTokens, getAccessToken, () => null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const bootstrapped = useRef(false);

  // 起動時（マウント時）に1回だけ実行: Access Tokenはメモリ保持のみのためリロードで消える。
  // Refresh Token（httpOnly Cookie）が有効ならrefreshAccessToken()でサイレントに再認証を試みる。
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    (async () => {
      let currentToken = getAccessToken();
      if (!currentToken) {
        // csrf_tokenはlogin/refresh成功時にのみ発行されるCookie。存在しない場合はRefresh Tokenも無い（未ログイン状態）と判断でき、
        // 失敗が確実な/api/auth/refresh呼び出しを省略する（コンソールへの400エラー出力を避けるため）。
        if (!getCsrfToken()) {
          setIsInitializing(false);
          return;
        }
        try {
          currentToken = await refreshAccessToken();
        } catch {
          setIsInitializing(false);
          return;
        }
      }
      try {
        const res = await axios.get(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setUser(res.data.data ?? res.data);
      } catch {
        clearTokens();
      } finally {
        setIsInitializing(false);
      }
    })();
  }, []);

  // 以後、何らかの理由（401リフレッシュ失敗等）でtokenがnullになった場合はユーザー状態もクリアする
  useEffect(() => {
    if (!token && user) {
      setUser(null);
    }
  }, [token, user]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await axios.post(`${API_BASE}/auth/login`, { username, password }, { withCredentials: true });
    const data = res.data.data ?? res.data;
    // Refresh TokenはSet-CookieでhttpOnly Cookieに保存される（JS側では扱わない）。
    // Access Tokenはtoken-storeがメモリ上にのみ保持する（sessionStorage等には保存しない）。
    setAccessToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    const currentToken = getAccessToken();
    const csrfToken = getCsrfToken();
    queryClient.clear();
    clearTokens();
    setUser(null);
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, {
        withCredentials: true,
        headers: {
          ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });
    } catch {
      // ログアウトAPIの失敗はクライアント側の状態クリアを妨げない
    }
  }, [queryClient]);


  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const currentToken = getAccessToken();
    if (!currentToken) {
      throw new Error('認証が無効です。再ログインしてください。');
    }
    await axios.post(
      `${API_BASE}/auth/change-password`,
      { currentPassword, newPassword },
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${currentToken}` },
      },
    );
  }, []);


  return (
    <AuthContext.Provider value={ {
      user,
      token,
      login,
      logout,

      changePassword,

      isAuthenticated: !!user,
      isInitializing,
    } }>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useAuthFetch() {
  const { logout } = useAuth();
  return useCallback(async (url: string, config: object = {}) => {
    const instance = axios.create();
    instance.interceptors.request.use(c => {
      const token = getAccessToken();
      if (token) c.headers.Authorization = `Bearer ${token}`;
      return c;
    });
    instance.interceptors.response.use(undefined, error => {
      if (error.response?.status === 401) {
        logout();
        throw new Error('認証が無効です。再ログインしてください。');
      }
      return Promise.reject(error);
    });
    return instance.get(url, config);
  }, [logout]);
}